import { randomKey } from "@sanity/util/content";
import { SUPPORTED_LANGUAGE_IDS } from "../common/localization";

const LANGUAGE_FIELD_NAME = "language";

export type LanguageFieldResult<T> = {
  transformed: T;
  fixed: number;
  fixedPaths: string[];
  unsupportedLanguages: string[];
};

function normalizePath(path: string): string {
  return path.replace(/\[\d+\]/g, "[]");
}

function isInternationalizedArrayValue(node: any): boolean {
  return (
    node &&
    typeof node === "object" &&
    typeof node._type === "string" &&
    node._type.startsWith("internationalizedArray") &&
    node._type.endsWith("Value")
  );
}

function isLegacyKeyedValue(node: any): boolean {
  if (!isInternationalizedArrayValue(node)) return false;

  const language = node[LANGUAGE_FIELD_NAME];
  if (typeof language === "string" && language.length > 0) return false;

  return typeof node._key === "string" && node._key.length > 0;
}

export function ensureLanguageField<T = any>(doc: T): LanguageFieldResult<T> {
  let fixedCount = 0;
  const rawPaths: string[] = [];
  const unsupportedLanguages = new Set<string>();

  function deepFix(value: any, path: string): any {
    if (Array.isArray(value)) {
      return value.map((item, i) => deepFix(item, `${path}[${i}]`));
    }

    if (value && typeof value === "object") {
      const rebuilt: any = {};
      for (const key of Object.keys(value)) {
        const childPath = path ? `${path}.${key}` : key;
        rebuilt[key] = deepFix(value[key], childPath);
      }
      if (isLegacyKeyedValue(rebuilt)) {
        fixedCount++;
        rawPaths.push(path);

        if (!(SUPPORTED_LANGUAGE_IDS as string[]).includes(rebuilt._key)) {
          unsupportedLanguages.add(rebuilt._key);
        }

        return {
          ...rebuilt,
          _key: randomKey(),
          [LANGUAGE_FIELD_NAME]: rebuilt._key
        };
      }

      return rebuilt;
    }

    return value;
  }

  const transformed = deepFix(doc, "");

  return {
    transformed,
    fixed: fixedCount,
    fixedPaths: [...new Set(rawPaths.map(normalizePath))].sort(),
    unsupportedLanguages: [...unsupportedLanguages].sort()
  };
}
